/**
 * Distribution — a small, token-driven horizontal "insight" strip every section screen uses to surface its
 * own at-a-glance breakdown (statut mix, deploy class, ticket category…). It is DERIVED, schema-free: each
 * screen feeds it counts computed from the rows it already fetched (no extra endpoint, no new table), so the
 * insight stays in lock-step with the table and never adds a network round-trip the tests must mock.
 *
 * Two faces share one tokenised vocabulary:
 *   • <DistributionBar> — a single stacked AA-contrast meter + a legend (count + share). Color is paired with
 *     a text label and a value, never color-only (daltonisme + médico-légal), and the meter carries an
 *     accessible role/aria so AT announces the composition.
 *   • <StatChips> — a row of compact label·value chips for headline figures that don't need a meter.
 *
 * Tones map onto the design tokens via the shared Badge palette names, so the colors theme for free across
 * pro/claire/dark. NEVER a raw hex (L-2). Numerics ride an LTR island so figures read identically FR/AR.
 */
import type { ReactNode } from "react";
import { token } from "@/design/theme";
import type { BadgeTone } from "@/ui";

/** One slice of a distribution: a localized label, a count, and a semantic tone. */
export interface DistributionSlice {
  key: string;
  label: ReactNode;
  value: number;
  tone: BadgeTone;
}

/** The token color for a tone (mirrors the Badge foreground palette — no raw hex). */
const TONE_COLOR: Record<BadgeTone, string> = {
  neutral: token.steel,
  success: token.success,
  warn: token.warn,
  danger: token.danger,
  info: token.azure,
  normal: token.normal,
  low: token.low,
  high: token.high,
  critical: token.critical,
  hold: token.hold,
};

function pct(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

/**
 * DistributionBar — a labelled stacked meter + legend over a set of slices. Renders its own empty affordance
 * when every slice is zero, so a screen can drop it in unconditionally. `total` defaults to the slice sum.
 */
export function DistributionBar({
  slices,
  total,
  emptyLabel,
  ariaLabel,
}: {
  slices: ReadonlyArray<DistributionSlice>;
  total?: number;
  emptyLabel: string;
  ariaLabel: string;
}) {
  const sum = total ?? slices.reduce((acc, s) => acc + s.value, 0);
  const shown = slices.filter((s) => s.value > 0);

  if (sum <= 0 || shown.length === 0) {
    return (
      <p style={{ margin: 0, color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
        {emptyLabel}
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
      <div
        role="img"
        aria-label={ariaLabel}
        style={{
          display: "flex",
          width: "100%",
          height: "0.5rem",
          borderRadius: "var(--nv-r-pill)",
          overflow: "hidden",
          background: "var(--nv-surface-2)",
        }}
      >
        {shown.map((s) => (
          <span
            key={s.key}
            aria-hidden
            style={{ width: `${pct(s.value, sum)}%`, background: TONE_COLOR[s.tone] }}
          />
        ))}
      </div>
      <ul
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--nv-s-2) var(--nv-s-4)",
          margin: 0,
          padding: 0,
          listStyle: "none",
        }}
      >
        {shown.map((s) => (
          <li key={s.key} style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
            <span
              aria-hidden
              style={{
                width: "0.625rem",
                height: "0.625rem",
                borderRadius: "var(--nv-r-xs)",
                background: TONE_COLOR[s.tone],
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-2, var(--nv-slate))" }}>
              {s.label}
            </span>
            <span
              className="nv-ltr-island"
              style={{
                fontFamily: "var(--nv-font-mono)",
                fontWeight: 600,
                fontSize: "var(--nv-fs-caption)",
                color: "var(--nv-text-0, var(--nv-ink))",
              }}
            >
              {s.value}
            </span>
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
              <span className="nv-ltr-island">{pct(s.value, sum)}%</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One headline figure for the <StatChips> row. */
export interface StatChip {
  key: string;
  label: ReactNode;
  value: ReactNode;
  tone?: BadgeTone;
}

/**
 * StatChips — a row of compact label·value chips for headline figures (total, filtered, oldest…). Tone tints
 * the value only; the label stays neutral so the chip reads as a metric, not a status badge.
 */
export function StatChips({ chips }: { chips: ReadonlyArray<StatChip> }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-3)" }}>
      {chips.map((c) => (
        <div
          key={c.key}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--nv-s-1)",
            minWidth: "5rem",
          }}
        >
          <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {c.label}
          </span>
          <span
            className="nv-ltr-island"
            style={{
              fontFamily: "var(--nv-font-mono)",
              fontWeight: 600,
              fontSize: "var(--nv-fs-h3, var(--nv-fs-title))",
              color: c.tone ? TONE_COLOR[c.tone] : "var(--nv-text-0, var(--nv-ink))",
              lineHeight: 1.1,
            }}
          >
            {c.value}
          </span>
        </div>
      ))}
    </div>
  );
}
